import React, { useMemo } from "react";
import { useTable, useSortBy, useExpanded } from "react-table";
import { Table } from "reactstrap";
import CustomPagination from "./CustomPagination";

const CommonTable = ({
  columns,
  data,
  defaultPageSize = 10,
  showPaginationBottom = true,
  activePage = 1,
  setPageNum,
  totalPages = 0,
  rowClick,
  className = "",
  rowClassName,
  expandedRowIndex,
  renderExpandedRow,
}) => {
  // 🔹 Convert columns to react-table format
  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
      {
        columns: memoColumns,
        data: memoData,
      },
      useSortBy,
      useExpanded,
    );

  return (
    <>
      <Table {...getTableProps()} responsive hover className={className}>
        {/* ---------- header ---------- */}
        <thead className="table-thead">
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.getHeaderGroupProps().key} {...(({ key, ...rest }) => rest)(headerGroup.getHeaderGroupProps())}>
              {headerGroup.headers.map((column) => (
                <th
                  key={column.getHeaderProps(column.getSortByToggleProps()).key}
                  {...(({ key, ...rest }) => rest)(column.getHeaderProps(
                    column.getSortByToggleProps()
                  ))}
                  style={{
                    width: column.width,
                    maxWidth: column.maxWidth,
                    minWidth: column.minWidth,
                    cursor: column.canSort ? "pointer" : "default",
                  }}
                  className={column.className || "align-top"}
                >
                  <div
                    className={`d-flex ${column.centerAlign
                      ? "justify-content-center align-items-center"
                      : column.rightAlign
                        ? "justify-content-end align-items-center"
                        : "align-items-center"
                      }`}
                  >
                    {column.render("Header")}

                    {column.canSort && (
                      <span className="ml-2">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <i className="fa fa-sort-down" />
                          ) : (
                            <i className="fa fa-sort-up" />
                          )
                        ) : (
                          <i className="fa fa-sort text-muted" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {/* ---------- body ---------- */}
        <tbody {...getTableBodyProps()}>
          {rows.length > 0 ? (
            rows.map((row, index) => {
              prepareRow(row);

              const isExpanded = expandedRowIndex === row.original.id;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    key={row.getRowProps().key}
                    {...(({ key, ...rest }) => rest)(row.getRowProps())}
                    onClick={() => rowClick?.(row.original)}
                    style={{
                      cursor:
                        !renderExpandedRow && rowClick ? "pointer" : "default",
                    }}
                    className={
                      rowClassName
                        ? rowClassName({
                            ...row.original,
                            index,
                            isExpanded,
                          })
                        : ""
                    }
                  >
                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...(({ key, ...rest }) => rest)(cell.getCellProps())}
                        style={{
                          width: cell.column.width,
                          maxWidth: cell.column.maxWidth,
                          minWidth: cell.column.minWidth,
                          overflow: "hidden",
                        }}
                        className={
                          cell.column.centerAlign
                            ? "text-center"
                            : cell.column.rightAlign
                              ? "text-right"
                              : "text-left"
                        }
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && renderExpandedRow && (
                    <tr>
                      <td colSpan={columns.length} className="p-3">
                        {renderExpandedRow(row.original)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center py-4">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* ---------- pagination ---------- */}
      {showPaginationBottom && totalPages > 1 && (
        <CustomPagination
          totalPages={totalPages}
          activePage={activePage}
          setPageNum={setPageNum}
        />
      )}
    </>
  );
};

export default CommonTable;
